import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../useAuth';
import UpgradeModal from '../components/UpgradeModal';

interface CompanyInfo {
  id: string;
  name: string;
  billingEmail: string;
  plan: string;
  tier?: string;
  billingInterval?: string;
  seatLimit: number;
  memberCount: number;
  ownerId: string;
  phone?: string;
  street?: string;
  city?: string;
  addressState?: string;
  zip?: string;
  website?: string;
  description?: string;
}

interface MemberUser {
  _id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  professionID?: string;
  licenses?: { professionID: string; stateID: number }[];
}

interface Member {
  memberId: string;
  userId: string;
  role: 'owner' | 'admin' | 'member';
  memberType: 'linked' | 'managed';
  inviteStatus: 'pending' | 'accepted';
  inviteEmail?: string;
  addedAt: string;
  user: MemberUser;
  activeCycles: number;
}

interface TeamAdmin {
  // accepted admins have userId; pending invites have memberId
  userId?: string;
  memberId?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  status: 'accepted' | 'pending';
}

type AddMode = 'linked' | 'managed';

const TeamDashboard: React.FC = () => {
  const { user: authUser } = useAuth();

  const [company, setCompany] = useState<CompanyInfo | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [admins, setAdmins] = useState<TeamAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Company name edit
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [nameSaving, setNameSaving] = useState(false);

  // Company profile edit
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    billingEmail: '', phone: '', street: '', city: '', addressState: '', zip: '', website: '', description: '',
  });
  const [profileSaving, setProfileSaving] = useState(false);

  // Upgrade modal (shown when user tries to add a member without a team plan)
  const [showTeamUpgradeModal, setShowTeamUpgradeModal] = useState(false);

  // Add member modal
  const [showAdd, setShowAdd] = useState(false);
  const [addMode, setAddMode] = useState<AddMode>('linked');
  const [addForm, setAddForm] = useState({ email: '', firstName: '', lastName: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');

  // Add team admin
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminAddLoading, setAdminAddLoading] = useState(false);
  const [adminAddError, setAdminAddError] = useState('');
  const [adminAddSuccess, setAdminAddSuccess] = useState('');

  // Remove confirmation
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [removingAdminId, setRemovingAdminId] = useState<string | null>(null);

  // Advanced view (inline compliance)
  const [advancedView, setAdvancedView] = useState(false);
  const [reportData, setReportData] = useState<any[] | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  const loadReport = async () => {
    if (reportData) return; // already loaded
    setReportLoading(true);
    try {
      const r = await api.get('/api/company/report');
      setReportData(r.data.members);
    } catch { /* silent */ }
    finally { setReportLoading(false); }
  };

  const toggleAdvanced = () => {
    const next = !advancedView;
    setAdvancedView(next);
    if (next) loadReport();
  };

  const load = async () => {
    try {
      const [cRes, mRes] = await Promise.all([
        api.get('/api/company/mine'),
        api.get('/api/company/members'),
      ]);
      setCompany(cRes.data);
      setNameInput(cRes.data.name);
      setMembers(mRes.data);

      // Load admins separately (may fail if not owner — that's OK)
      try {
        const aRes = await api.get('/api/company/admins');
        setAdmins(aRes.data);
      } catch {
        setAdmins([]);
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load team data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const isOwner = !!(authUser && company && company.ownerId === authUser.id);
  const isSiteAdmin = authUser?.role === 'admin';
  const canManageAdmins = isOwner || isSiteAdmin;

  const saveName = async () => {
    if (!nameInput.trim()) return;
    setNameSaving(true);
    try {
      await api.patch('/api/company/mine', { name: nameInput.trim() });
      setCompany(prev => prev ? { ...prev, name: nameInput.trim() } : prev);
      setEditingName(false);
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to save.');
    } finally {
      setNameSaving(false);
    }
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      const res = await api.patch('/api/company/mine', profileForm);
      setCompany(prev => prev ? { ...prev, ...profileForm, billingEmail: res.data.billingEmail } : prev);
      setEditingProfile(false);
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to save profile.');
    } finally {
      setProfileSaving(false);
    }
  };

  const addMember = async () => {
    setAddError('');
    setAddLoading(true);
    try {
      const endpoint = addMode === 'linked'
        ? '/api/company/members/invite'
        : '/api/company/members/managed';
      const res = await api.post(endpoint, addForm);
      setShowAdd(false);
      setAddForm({ email: '', firstName: '', lastName: '' });
      if (res.data?.status === 'invited') {
        alert(`Invite sent to ${addForm.email}. They'll appear as pending until they register.`);
      }
      await load();
    } catch (e: any) {
      setAddError(e.response?.data?.error || 'Failed to add member.');
    } finally {
      setAddLoading(false);
    }
  };

  const addAdmin = async () => {
    setAdminAddError('');
    setAdminAddSuccess('');
    setAdminAddLoading(true);
    try {
      const res = await api.post('/api/company/admins', { email: adminEmail.trim() });
      const data = res.data;
      if (data.status === 'invited') {
        // Pending invite — add to list with pending status
        setAdmins(prev => [...prev, {
          memberId: data.memberId,
          email: data.email,
          status: 'pending',
        }]);
        setAdminAddSuccess(`Invite sent to ${data.email}. They'll get admin access once they create their account.`);
        setAdminEmail('');
      } else {
        // Immediate add
        setAdmins(prev => [...prev, {
          userId: String(data.userId),
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          status: 'accepted',
        }]);
        setShowAddAdmin(false);
        setAdminEmail('');
      }
    } catch (e: any) {
      setAdminAddError(e.response?.data?.error || 'Failed to add team admin.');
    } finally {
      setAdminAddLoading(false);
    }
  };

  const removeAdmin = async (admin: TeamAdmin) => {
    if (!window.confirm('Remove this team admin? They will lose team management access.')) return;
    const id = admin.userId || admin.memberId || '';
    setRemovingAdminId(id);
    try {
      if (admin.status === 'pending' && admin.memberId) {
        await api.delete(`/api/company/invites/${admin.memberId}`);
      } else {
        await api.delete(`/api/company/admins/${admin.userId}`);
      }
      setAdmins(prev => prev.filter(a => (a.userId || a.memberId) !== id));
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to remove admin.');
    } finally {
      setRemovingAdminId(null);
    }
  };

  const resendInvite = async (memberId: string, email: string) => {
    setResendingId(memberId);
    try {
      await api.post(`/api/company/members/${memberId}/resend-invite`);
      alert(`Invite email resent to ${email}.`);
      await load();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to resend invite.');
    } finally {
      setResendingId(null);
    }
  };

  const cancelInvite = async (memberId: string) => {
    try {
      await api.delete(`/api/company/invites/${memberId}`);
      setMembers(prev => prev.filter(m => m.memberId !== memberId));
      if (company) setCompany({ ...company, memberCount: company.memberCount - 1 });
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to cancel invite.');
    }
  };

  const removeMember = async (memberId: string) => {
    setRemovingId(memberId);
    try {
      await api.delete(`/api/company/members/${memberId}`);
      setMembers(prev => prev.filter(m => m.memberId !== memberId));
      if (company) setCompany({ ...company, memberCount: company.memberCount - 1 });
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to remove member.');
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) return (
    <div className="container mt-5 text-center">
      <div className="spinner-border text-primary" role="status" />
    </div>
  );

  if (error) return (
    <div className="container mt-5">
      <div className="alert alert-danger">{error}</div>
      {error.includes('Team plan') && (
        <Link to="/pricing" className="btn btn-primary">Upgrade to Team</Link>
      )}
    </div>
  );

  const seatsLeft = (company?.seatLimit ?? 5) - (company?.memberCount ?? 0);

  return (
    <div className="container mt-4 pb-5">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          {editingName ? (
            <div className="d-flex align-items-center gap-2">
              <input
                className="form-control"
                style={{ fontSize: '1.4rem', fontWeight: 700, width: 280 }}
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveName()}
                autoFocus
              />
              <button className="btn btn-primary btn-sm" onClick={saveName} disabled={nameSaving}>
                {nameSaving ? 'Saving…' : 'Save'}
              </button>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => setEditingName(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <h3 className="mb-0 d-flex align-items-center gap-2">
              {company?.name}
              {canManageAdmins && (
                <button
                  className="btn btn-link btn-sm p-0 text-muted"
                  style={{ fontSize: '0.8rem' }}
                  onClick={() => setEditingName(true)}
                >
                  Edit
                </button>
              )}
            </h3>
          )}
          <p className="text-muted mb-0 small mt-1">Team / Firm Dashboard</p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Link to="/team/help" className="btn btn-outline-secondary btn-sm">? Help</Link>
          <Link to="/team/report" className="btn btn-outline-primary btn-sm">📋 Full Report</Link>
          <Link to="/team/session/new" className="btn btn-success btn-sm">+ Add Firm Session</Link>
          {canManageAdmins && (
            <Link to="/billing" className="btn btn-outline-secondary btn-sm">← Billing</Link>
          )}
        </div>
      </div>

      {/* Company Profile */}
      <div className="card mb-4">
        <div className="card-header cec-section-header">
          <span className="cec-section-title">Company Profile</span>
          {canManageAdmins && !editingProfile && (
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => {
                setProfileForm({
                  billingEmail: company?.billingEmail || '',
                  phone: company?.phone || '',
                  street: company?.street || '',
                  city: company?.city || '',
                  addressState: company?.addressState || '',
                  zip: company?.zip || '',
                  website: company?.website || '',
                  description: company?.description || '',
                });
                setEditingProfile(true);
              }}
            >
              Edit
            </button>
          )}
        </div>
        <div className="card-body">
          {editingProfile ? (
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Billing Email</label>
                <input type="email" className="form-control" value={profileForm.billingEmail}
                  onChange={e => setProfileForm(f => ({ ...f, billingEmail: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Phone</label>
                <input className="form-control" value={profileForm.phone}
                  onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div className="col-12">
                <label className="form-label">Street Address</label>
                <input className="form-control" value={profileForm.street}
                  onChange={e => setProfileForm(f => ({ ...f, street: e.target.value }))} />
              </div>
              <div className="col-md-5">
                <label className="form-label">City</label>
                <input className="form-control" value={profileForm.city}
                  onChange={e => setProfileForm(f => ({ ...f, city: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label className="form-label">State</label>
                <input className="form-control" maxLength={2} value={profileForm.addressState}
                  onChange={e => setProfileForm(f => ({ ...f, addressState: e.target.value.toUpperCase() }))} />
              </div>
              <div className="col-md-4">
                <label className="form-label">ZIP</label>
                <input className="form-control" value={profileForm.zip}
                  onChange={e => setProfileForm(f => ({ ...f, zip: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Website</label>
                <input className="form-control" placeholder="https://yourfirm.com" value={profileForm.website}
                  onChange={e => setProfileForm(f => ({ ...f, website: e.target.value }))} />
              </div>
              <div className="col-12">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={2} value={profileForm.description}
                  onChange={e => setProfileForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="col-12 d-flex gap-2">
                <button className="btn btn-primary btn-sm" onClick={saveProfile} disabled={profileSaving}>
                  {profileSaving ? 'Saving…' : 'Save Profile'}
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setEditingProfile(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="row g-2">
              <div className="col-md-6">
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {company?.tier && (
                    <span className="badge bg-primary" style={{ fontSize: '0.8rem' }}>
                      {company.tier.charAt(0).toUpperCase() + company.tier.slice(1)}
                    </span>
                  )}
                  {company?.billingInterval && (
                    <span className="badge bg-light text-dark border" style={{ fontSize: '0.8rem' }}>
                      {company.billingInterval}
                    </span>
                  )}
                  <span className={`badge ${company?.plan === 'active' ? 'bg-success' : company?.plan === 'past_due' ? 'bg-warning text-dark' : 'bg-secondary'}`} style={{ fontSize: '0.8rem' }}>
                    {company?.plan || 'trial'}
                  </span>
                </div>
                <div className="small text-muted d-flex flex-column gap-1">
                  <span>📧 {company?.billingEmail}</span>
                  {company?.phone && <span>📞 {company.phone}</span>}
                  {company?.website && (
                    <span>🌐 <a href={company.website} target="_blank" rel="noopener noreferrer">{company.website}</a></span>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                {(company?.street || company?.city) && (
                  <div className="small text-muted">
                    <div>📍</div>
                    {company?.street && <div>{company.street}</div>}
                    {(company?.city || company?.addressState || company?.zip) && (
                      <div>{[company?.city, company?.addressState, company?.zip].filter(Boolean).join(', ')}</div>
                    )}
                  </div>
                )}
                {company?.description && (
                  <div className="small text-muted mt-2" style={{ fontStyle: 'italic' }}>{company.description}</div>
                )}
                {!company?.street && !company?.city && !company?.description && !company?.phone && !company?.website && canManageAdmins && (
                  <div className="small text-muted fst-italic">No profile details yet — click Edit to add address and contact info.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Seat usage bar */}
      <div className="card mb-4">
        <div className="card-header cec-section-header">
          <span className="cec-section-title">Seats</span>
          <small>{company?.memberCount} of {company?.seatLimit} used{seatsLeft > 0 ? ` · ${seatsLeft} remaining` : ' · Full'}</small>
        </div>
        <div className="card-body p-3">
          <div className="progress" style={{ height: 8 }}>
            <div
              className={`progress-bar ${seatsLeft === 0 ? 'bg-danger' : seatsLeft <= 1 ? 'bg-warning' : 'bg-success'}`}
              style={{ width: `${((company?.memberCount ?? 0) / (company?.seatLimit ?? 5)) * 100}%` }}
            />
          </div>
          <p className="text-muted small mb-0 mt-2">
            Base plan includes {company?.seatLimit} seats. Additional seats are $5/user/mo — contact us to expand.
          </p>
        </div>
      </div>

      {/* Team Admins (owner/site-admin only) */}
      {canManageAdmins && (
        <div className="card shadow-sm mb-4">
          <div className="card-header cec-section-header">
            <span className="cec-section-title">Team Admins</span>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => { setShowAddAdmin(true); setAdminAddError(''); setAdminAddSuccess(''); setAdminEmail(''); }}
            >
              + Add Admin
            </button>
          </div>
          <div className="card-body p-0">
            {admins.length === 0 ? (
              <div className="p-3 text-muted small">
                No team admins assigned. Admins can manage members and firm sessions on your behalf.
              </div>
            ) : (
              <table className="table cec-table table-hover mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map(a => {
                    const adminKey = a.userId || a.memberId || a.email;
                    const isRemoving = removingAdminId === (a.userId || a.memberId);
                    return (
                      <tr key={adminKey}>
                        <td className="fw-medium">
                          {a.status === 'pending'
                            ? <span className="text-muted fst-italic">Invite pending</span>
                            : [a.firstName, a.lastName].filter(Boolean).join(' ') || '—'}
                        </td>
                        <td className="small">
                          {a.email}
                          {a.status === 'pending' && (
                            <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.7rem' }}>Pending</span>
                          )}
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-outline-danger btn-sm"
                            style={{ fontSize: '0.75rem' }}
                            disabled={isRemoving}
                            onClick={() => removeAdmin(a)}
                          >
                            {isRemoving ? '…' : a.status === 'pending' ? 'Cancel Invite' : 'Remove'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Members table */}
      <div className="card shadow-sm">
        <div className="card-header cec-section-header flex-wrap gap-2">
          <span className="cec-section-title">Team Members</span>
          <div className="d-flex gap-2 align-items-center">
            <button
              className={`btn btn-sm ${advancedView ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={toggleAdvanced}
              title="Toggle compliance details inline"
            >
              {advancedView ? '▲ Simple View' : '▼ Advanced View'}
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                if (company?.plan !== 'team' && authUser?.subscription?.plan !== 'team') {
                  setShowTeamUpgradeModal(true);
                } else {
                  setShowAdd(true);
                  setAddError('');
                  setAddForm({ email: '', firstName: '', lastName: '' });
                }
              }}
              disabled={company?.plan === 'team' && seatsLeft <= 0}
            >
              + Add Member
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table cec-table table-hover mb-0 align-middle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Type</th>
                <th>Profession</th>
                <th>Licenses</th>
                <th>Active Cycles</th>
                <th>Role</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    No members yet. Add your first team member.
                  </td>
                </tr>
              )}
              {members.map(m => {
                const u = m.user;
                const isPending = m.inviteStatus === 'pending';
                const displayName = u
                  ? [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email
                  : m.inviteEmail || '—';
                const rpt = reportData?.find((r: any) => r.memberId === m.memberId);
                return (
                  <React.Fragment key={m.memberId}>
                  <tr style={isPending ? { opacity: 0.7 } : undefined}>
                    <td className="fw-medium">
                      {displayName}
                      {isPending && (
                        <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.65rem' }}>
                          Invite Pending
                        </span>
                      )}
                    </td>
                    <td className="text-muted small">{u?.email || m.inviteEmail || '—'}</td>
                    <td>
                      <span className={`badge ${m.memberType === 'managed' ? 'bg-secondary' : 'bg-info text-dark'}`}>
                        {m.memberType}
                      </span>
                    </td>
                    <td className="small">{u?.professionID || u?.licenses?.[0]?.professionID || '—'}</td>
                    <td className="small">{isPending ? '—' : (u?.licenses?.length ?? 0)}</td>
                    <td className="small">{isPending ? '—' : m.activeCycles}</td>
                    <td>
                      <span className={`badge ${m.role === 'owner' ? 'bg-primary' : 'bg-light text-dark border'}`}>
                        {m.role}
                      </span>
                    </td>
                    <td className="text-end">
                      <div className="d-flex gap-1 justify-content-end flex-wrap">
                        {!isPending && u?._id && (
                          <Link
                            to={`/admin/compliance-report/${u._id}?context=team`}
                            className="btn btn-outline-secondary btn-sm"
                            style={{ fontSize: '0.75rem' }}
                          >
                            Report
                          </Link>
                        )}
                        {!isPending && m.memberType === 'managed' && u?._id && (
                          <Link
                            to={`/team/members/${m.memberId}`}
                            className="btn btn-outline-primary btn-sm"
                            style={{ fontSize: '0.75rem' }}
                          >
                            Manage
                          </Link>
                        )}
                        {(isPending || m.memberType === 'managed') && m.role !== 'owner' && (
                          <button
                            className="btn btn-outline-info btn-sm"
                            style={{ fontSize: '0.75rem' }}
                            disabled={resendingId === m.memberId}
                            onClick={() => resendInvite(m.memberId, m.inviteEmail || u?.email || '')}
                          >
                            {resendingId === m.memberId ? '…' : isPending ? 'Resend' : 'Send Invite'}
                          </button>
                        )}
                        {isPending ? (
                          <button
                            className="btn btn-outline-warning btn-sm"
                            style={{ fontSize: '0.75rem' }}
                            onClick={() => {
                              if (window.confirm(`Cancel invite for ${m.inviteEmail}?`)) {
                                cancelInvite(m.memberId);
                              }
                            }}
                          >
                            Cancel
                          </button>
                        ) : m.role !== 'owner' && (
                          <button
                            className="btn btn-outline-danger btn-sm"
                            style={{ fontSize: '0.75rem' }}
                            disabled={removingId === m.memberId}
                            onClick={() => {
                              if (window.confirm(`Remove ${displayName} from the team?`)) {
                                removeMember(m.memberId);
                              }
                            }}
                          >
                            {removingId === m.memberId ? '…' : 'Remove'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {advancedView && (
                    <tr className="cec-table-subrow">
                      <td colSpan={8} className="px-4 py-3">
                        {reportLoading && !rpt ? (
                          <span className="text-muted small">Loading compliance…</span>
                        ) : !rpt ? (
                          <span className="text-muted small">No compliance data available.</span>
                        ) : (
                          <div>
                            {/* Licenses */}
                            {rpt.licenses?.length > 0 && (
                              <div className="mb-2 d-flex flex-wrap gap-2 align-items-center">
                                <span className="text-muted small fw-semibold me-1">Licenses:</span>
                                {rpt.licenses.map((lic: any) => (
                                  <span key={lic._id} className="badge bg-light text-dark border" style={{ fontSize: '0.72rem' }}>
                                    {lic.professionID} · State {lic.stateID} · #{lic.licenseNumber}
                                    <span className={`ms-1 badge ${lic.status === 'active' ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.6rem' }}>{lic.status}</span>
                                  </span>
                                ))}
                              </div>
                            )}
                            {/* Compliance cycles */}
                            {rpt.compliance?.length === 0 ? (
                              <span className="text-muted small">No active compliance cycles.</span>
                            ) : (
                              <div className="d-flex flex-wrap gap-3">
                                {rpt.compliance?.map((c: any) => {
                                  const pct = Math.min(c.percentComplete, 100);
                                  const barColor = c.isMet ? '#198754' : pct >= 60 ? '#ffc107' : '#dc3545';
                                  return (
                                    <div key={c.cycleId} className="border rounded p-2" style={{ minWidth: 220, maxWidth: 280, background: '#fff' }}>
                                      <div className="fw-semibold" style={{ fontSize: '0.8rem' }}>{c.cycleName}</div>
                                      <div className="text-muted" style={{ fontSize: '0.72rem' }}>{c.professionName} · {c.stateName}</div>
                                      <div className="d-flex justify-content-between align-items-center mt-1 mb-1">
                                        <span style={{ fontSize: '0.75rem', color: barColor, fontWeight: 600 }}>
                                          {c.totalEarned}/{c.totalRequired} credits
                                        </span>
                                        <span style={{ fontSize: '0.7rem', color: '#6c757d' }}>
                                          {c.isMet ? '✓ Met' : `${c.daysRemaining}d left`}
                                        </span>
                                      </div>
                                      <div className="progress" style={{ height: 6, borderRadius: 4 }}>
                                        <div
                                          className="progress-bar"
                                          style={{ width: `${pct}%`, background: barColor, borderRadius: 4 }}
                                        />
                                      </div>
                                      {c.categories?.filter((cat: any) => cat.required > 0).map((cat: any) => (
                                        <div key={cat.categoryTag} className="d-flex justify-content-between mt-1" style={{ fontSize: '0.68rem', color: cat.isMet ? '#198754' : '#856404' }}>
                                          <span>{cat.isMet ? '✓' : '○'} {cat.categoryName}</span>
                                          <span>{cat.earned}/{cat.required}</span>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAdd && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={e => { if (e.target === e.currentTarget) setShowAdd(false); }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Team Member</h5>
                <button className="btn-close" onClick={() => setShowAdd(false)} />
              </div>
              <div className="modal-body">
                <div className="d-flex gap-2 mb-4">
                  <button
                    className={`btn btn-sm flex-fill ${addMode === 'linked' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setAddMode('linked')}
                  >
                    Invite by Email
                  </button>
                  <button
                    className={`btn btn-sm flex-fill ${addMode === 'managed' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setAddMode('managed')}
                  >
                    Create Managed Profile
                  </button>
                </div>

                {addError && <div className="alert alert-danger py-2 small">{addError}</div>}

                {addMode === 'linked' ? (
                  <div>
                    <p className="text-muted small mb-3">
                      Enter their email. If they already have a CEC Track account they'll be added instantly. If not, we'll send them an invite to create one.
                    </p>
                    <label className="form-label">Email address</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="colleague@example.com"
                      value={addForm.email}
                      onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                    />
                  </div>
                ) : (
                  <div>
                    <p className="text-muted small mb-3">
                      Create a managed profile for a staff member who doesn't have their own account. You control the data; they won't have login access.
                    </p>
                    <div className="row g-2 mb-2">
                      <div className="col">
                        <label className="form-label">First name</label>
                        <input
                          className="form-control"
                          value={addForm.firstName}
                          onChange={e => setAddForm(f => ({ ...f, firstName: e.target.value }))}
                        />
                      </div>
                      <div className="col">
                        <label className="form-label">Last name</label>
                        <input
                          className="form-control"
                          value={addForm.lastName}
                          onChange={e => setAddForm(f => ({ ...f, lastName: e.target.value }))}
                        />
                      </div>
                    </div>
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="staff@yourfirm.com"
                      value={addForm.email}
                      onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowAdd(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={addMember}
                  disabled={addLoading || !addForm.email || (addMode === 'managed' && (!addForm.firstName || !addForm.lastName))}
                >
                  {addLoading ? 'Adding…' : addMode === 'linked' ? 'Add Member' : 'Create Profile'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Admin Modal */}
      {showAddAdmin && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={e => { if (e.target === e.currentTarget) setShowAddAdmin(false); }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Team Admin</h5>
                <button className="btn-close" onClick={() => setShowAddAdmin(false)} />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  Team admins can add/remove members and manage firm sessions. If they don't have a CEC Track account yet, an invite email will be sent.
                </p>
                {adminAddError && <div className="alert alert-danger py-2 small">{adminAddError}</div>}
                {adminAddSuccess && <div className="alert alert-success py-2 small">{adminAddSuccess}</div>}
                <label className="form-label">Email address</label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="admin@yourfirm.com"
                  value={adminEmail}
                  onChange={e => setAdminEmail(e.target.value)}
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowAddAdmin(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={addAdmin}
                  disabled={adminAddLoading || !adminEmail.trim()}
                >
                  {adminAddLoading ? 'Adding…' : 'Add Admin'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTeamUpgradeModal && (
        <UpgradeModal
          type="team"
          onClose={() => setShowTeamUpgradeModal(false)}
        />
      )}
    </div>
  );
};

export default TeamDashboard;
